import { ActionReducerMap, MetaReducer } from '@ngrx/store';
import { AppState } from './store/app-state';
import { AtcEffects } from './store/atc/atc.effects';
import { initialLoadingState, loadingStateReducer } from './store/loading-state/loading-state.reducer';

import { ApiMetadataValidatorEffects } from './store/api-metadata-validator/api-metadata-validator.effects';
import { apiMetadataValidatorReducer } from './store/api-metadata-validator/api-metadata-validator.reducer';
import { atcReducer } from './store/atc/atc.reducer';
import { CentralPolicyResultsEffects } from './store/central-policy-results/central-policy-results.effects';
import { CentralPolicyResultsReducer } from './store/central-policy-results/central-policy-results.reducer';
import { CentralPolicyUsageEffects } from './store/central-policy-usage/central-policy-usage.effects';
import { CentralPolicyUsageReducer } from './store/central-policy-usage/central-policy-usage.reducer';
import { CodeCoverageInfoEffects } from './store/code-coverage-info/code-coverage-info.effects';
import { codeCoverageInfoReducer } from './store/code-coverage-info/code-coverage-info.reducer';
import { ComplianceCertificatesEffects } from './store/compliance-certificates/compliance-certificates.effects';
import { complianceCertificatesReducer } from './store/compliance-certificates/compliance-certificates.reducer';
import { ConfigurationEffects } from './store/configuration/configuration.effects';
import { configurationReducer } from './store/configuration/configuration.reducer';
import { CustomPolicyResultsEffects } from './store/custom-policy-results/custom-policy-results.effects';
import { CustomPolicyResultsReducer } from './store/custom-policy-results/custom-policy-results.reducer';
import { CustomPolicyUsageEffects } from './store/custom-policy-usage/custom-policy-usage.effects';
import { CustomPolicyUsageReducer } from './store/custom-policy-usage/custom-policy-usage.reducer';
import { DeliveriesEffects } from './store/deliveries/deliveries.effects';
import { deliveriesReducer } from './store/deliveries/deliveries.reducer';
import { DocumentsEffects } from './store/documents/documents.effects';
import { documentsReducer } from './store/documents/documents.reducer';
import { EnvironmentEffects } from './store/environment/environment.effects';
import { environmentReducer } from './store/environment/environment.reducer';
import { Fc2Effects } from './store/fc-2/fc-2.effects';
import { fc2Reducer } from './store/fc-2/fc-2.reducer';
import { FosstarsEffects } from './store/fosstars/fosstars.effects';
import { fosstarsReducer } from './store/fosstars/fosstars.reducer';
import {
  initialApiMetadataValidatorSlice,
  initialAtcSlice,
  initialCentralPolicyResultsSlice,
  initialCentralPolicyUsageSlice,
  initialCodeCoverageInfoSlice,
  initialComplianceCertificatesSlice,
  initialConfigurationSlice,
  initialCustomPolicyResultsSlice,
  initialCustomPolicyUsageSlice,
  initialDeliveriesSlice,
  initialDocumentsSlice,
  initialEnvironmentSlice,
  initialFc2Slice,
  initialFosstarsSlice,
  initialLogAnalysisSlice,
  initialMalwarescanSlice,
  initialMicrodeliveriesSlice,
  initialPipelineRunSlice,
  initialPipelineSlice,
  initialProcessingStatusSlice,
  initialRoutingSlice,
  initialSonarQubeCoverageSlice,
  initialTestRegressionSlice,
} from './store/initial-slices';
import { MalwarescanEffects } from './store/malwarescan/malwarescan.effects';
import { malwarescanReducer } from './store/malwarescan/malwarescan.reducer';
import { MicrodeliveriesEffects } from './store/microdeliveries/microdeliveries.effects';
import { microdeliveriesReducer } from './store/microdeliveries/microdeliveries.reducer';
import { PipelineRunEffects } from './store/pipeline-run/pipeline-run.effects';
import { pipelineRunReducer } from './store/pipeline-run/pipeline-run.reducer';
import { PipelineEffects } from './store/pipeline/pipeline.effects';
import { pipelineReducer } from './store/pipeline/pipeline.reducer';
import { ProcessingStatusEffects } from './store/processing-status/processing-status.effects';
import { processingStatusReducer } from './store/processing-status/processing-status.reducer';
import { SonarQubeCoverageEffects } from './store/sonar-qube-coverage/sonar-qube-coverage.effects';
import { sonarQubeCoverageReducer } from './store/sonar-qube-coverage/sonar-qube-coverage.reducer';
import { TestRegressionEffects } from './store/test-regression/test-regression.effects';
import { testRegresionReducer } from './store/test-regression/test-regression.reducer';
import { RoutingEffects } from './store/routing/routing.effects';
import { routingReducer } from './store/routing/routing.reducer';
import { logAnalysisReducer } from './store/log-analysis/log-analysis.reducer';
import { LogAnalysisEffects } from './store/log-analysis/log-analysis.effects';

/**
 * Is needed to initialize the mockStore
 */
export const initialAppState: AppState = {
  loadingStateSlice: initialLoadingState,
  fc2Slice: initialFc2Slice,
  atcSlice: initialAtcSlice,
  sonarQubeCoverageSlice: initialSonarQubeCoverageSlice,
  codeCoverageInfoSlice: initialCodeCoverageInfoSlice,
  microdeliveriesSlice: initialMicrodeliveriesSlice,
  deliveriesSlice: initialDeliveriesSlice,
  pipelineRunSlice: initialPipelineRunSlice,
  pipelineSlice: initialPipelineSlice,
  configurationSlice: initialConfigurationSlice,
  testRegressionSlice: initialTestRegressionSlice,
  malwarescanSlice: initialMalwarescanSlice,
  documentsSlice: initialDocumentsSlice,
  complianceCertificatesSlice: initialComplianceCertificatesSlice,
  processingStatusSlice: initialProcessingStatusSlice,
  apiMetadataValidatorSlice: initialApiMetadataValidatorSlice,
  fosstarsSlice: initialFosstarsSlice,
  centralPolicyResultsSlice: initialCentralPolicyResultsSlice,
  customPolicyResultsSlice: initialCustomPolicyResultsSlice,
  centralPolicyUsageSlice: initialCentralPolicyUsageSlice,
  customPolicyUsageSlice: initialCustomPolicyUsageSlice,
  environmentSlice: initialEnvironmentSlice,
  routingSlice: initialRoutingSlice,
  logAnalysisSlice: initialLogAnalysisSlice,
};

export const metaReducers: Array<MetaReducer<AppState>> = [];

export const reducers: ActionReducerMap<AppState> = {
  loadingStateSlice: loadingStateReducer,
  fc2Slice: fc2Reducer,
  atcSlice: atcReducer,
  sonarQubeCoverageSlice: sonarQubeCoverageReducer,
  codeCoverageInfoSlice: codeCoverageInfoReducer,
  microdeliveriesSlice: microdeliveriesReducer,
  deliveriesSlice: deliveriesReducer,
  pipelineRunSlice: pipelineRunReducer,
  pipelineSlice: pipelineReducer,
  configurationSlice: configurationReducer,
  testRegressionSlice: testRegresionReducer,
  malwarescanSlice: malwarescanReducer,
  documentsSlice: documentsReducer,
  complianceCertificatesSlice: complianceCertificatesReducer,
  processingStatusSlice: processingStatusReducer,
  apiMetadataValidatorSlice: apiMetadataValidatorReducer,
  fosstarsSlice: fosstarsReducer,
  centralPolicyResultsSlice: CentralPolicyResultsReducer,
  customPolicyResultsSlice: CustomPolicyResultsReducer,
  centralPolicyUsageSlice: CentralPolicyUsageReducer,
  customPolicyUsageSlice: CustomPolicyUsageReducer,
  environmentSlice: environmentReducer,
  routingSlice: routingReducer,
  logAnalysisSlice: logAnalysisReducer,
};

export const effects = [
  Fc2Effects,
  AtcEffects,
  SonarQubeCoverageEffects,
  CodeCoverageInfoEffects,
  MicrodeliveriesEffects,
  DeliveriesEffects,
  PipelineRunEffects,
  PipelineEffects,
  ConfigurationEffects,
  TestRegressionEffects,
  MalwarescanEffects,
  DocumentsEffects,
  ComplianceCertificatesEffects,
  ProcessingStatusEffects,
  ApiMetadataValidatorEffects,
  FosstarsEffects,
  CentralPolicyResultsEffects,
  CustomPolicyResultsEffects,
  CentralPolicyUsageEffects,
  CustomPolicyUsageEffects,
  EnvironmentEffects,
  RoutingEffects,
  LogAnalysisEffects,
];
